import { useState } from 'react';
import PropTypes from 'prop-types';

import Alert from '@mui/material/Alert';
import Collapse from '@mui/material/Collapse';
import AlertTitle from '@mui/material/AlertTitle';
import IconButton from '@mui/material/IconButton';
import LoadingButton from '@mui/lab/LoadingButton';

import { usePathname } from 'src/routes/hooks';

import Iconify from '../iconify';

// ----------------------------------------------------------------------

/**
 * Reusable announcement banner component
 * 
 * Features:
 * - Persistent dismissal via localStorage
 * - Conditional visibility based on custom logic
 * - Optional action button with route-based visibility
 * - Configurable severity and content
 * 
 * @param {string} storageKey - Unique key for localStorage persistence
 * @param {string} title - Banner title (bold)
 * @param {string} message - Banner message content
 * @param {boolean} showCondition - Condition to show the banner (e.g., hasIntegration)
 * @param {string} severity - Alert severity: 'info' | 'warning' | 'error' | 'success'
 * @param {object} action - Optional action button configuration
 * @param {string} action.label - Button text
 * @param {function} action.onClick - Button click handler
 * @param {boolean} action.loading - Show a spinner while the click is still working
 * @param {string[]} action.hideOnRoutes - Routes where button should be hidden
 * @param {boolean} dismissable - Whether the banner can be dismissed (default: true)
 */
export default function AnnouncementBanner ({
  storageKey,
  title,
  message,
  showCondition = true,
  severity = 'info',
  action = null,
  dismissable = true,
}) {
  const pathname = usePathname();

  // Check if banner was previously dismissed from localStorage
  const [dismissed, setDismissed] = useState(() => {
    if (!dismissable) return false;
    try {
      const stored = localStorage.getItem(storageKey);
      return stored === 'true';
    } catch (error) {
      return false;
    }
  });

  // Don't show if dismissed or condition not met
  if (dismissed || !showCondition) {
    return null;
  }

  const handleDismiss = () => {
    setDismissed(true);
    try {
      localStorage.setItem(storageKey, 'true');
    } catch (error) {
      console.error('Failed to save dismissed state:', error);
    }
  };

  // Check if action button should be hidden on current route
  const shouldShowActionButton = action && (!action.hideOnRoutes || !action.hideOnRoutes.includes(pathname));

  return (
    <Collapse in>
      <Alert
        severity={severity}
        action={
          <>
            {shouldShowActionButton && (
              <LoadingButton
                color={severity}
                size="medium"
                variant="contained"
                loading={action.loading}
                onClick={action.onClick}
                sx={{
                  mr: 1,
                }}
              >
                {action.label}
              </LoadingButton>
            )}
            {dismissable && (
              <IconButton
                aria-label="close"
                color="inherit"
                size="medium"
                onClick={handleDismiss}
                sx={{
                  alignSelf: 'flex-start',
                }}
              >
                <Iconify icon="eva:close-fill" />
              </IconButton>
            )}
          </>
        }
        sx={{
          mb: 3,
          '& .MuiAlert-action': {
            alignItems: 'flex-start',
            paddingTop: 0.5,
          }
        }}
      >
        {title && (
          <AlertTitle>
            <strong>{title}</strong>
          </AlertTitle>
        )}
        {message}
      </Alert>
    </Collapse>
  );
}

AnnouncementBanner.propTypes = {
  storageKey: PropTypes.string.isRequired,
  title: PropTypes.string,
  message: PropTypes.string.isRequired,
  showCondition: PropTypes.bool,
  severity: PropTypes.oneOf(['info', 'warning', 'error', 'success']),
  action: PropTypes.shape({
    label: PropTypes.string.isRequired,
    onClick: PropTypes.func.isRequired,
    loading: PropTypes.bool,
    hideOnRoutes: PropTypes.arrayOf(PropTypes.string),
  }),
  dismissable: PropTypes.bool,
};

